import { ExternalLink, Mail, UserX } from "lucide-react";
import type { ReactNode } from "react";

import { AvatarPlaceholder } from "components/AvatarPlaceholder";
import { LinkedInIcon } from "components/LinkedInIcon";
import { LoadingRows } from "components/states";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "components/ui/sheet";

// The session, not useAuthUser: see use-event-person.ts.
import { useSession } from "../auth/session";
import { ConnectWithPerson } from "../connections/ConnectControls";
import { PersonNotesSection } from "../person-notes/PersonNotesSection";
import { SignalBadges } from "./SignalBadges";
import { safeLinkedInUrl } from "./external-url";
import {
  useEventPerson,
  type EventPerson,
  type EventPersonTarget,
} from "./use-event-person";

export type { EventPersonTarget } from "./use-event-person";

/**
 * The one view of a person at an event, wherever they are opened from: the
 * attendee list, a dinner table, the Now tab, the speaker list, a track
 * champion, the connections page, and the organiser's Requests tab.
 *
 * Callers pass ids only. The sheet loads the person itself (see
 * `useEventPerson`) and applies the same rules everywhere: contact details
 * follow the event's Attendees switch, the private note shows for anybody
 * with a roster row but the viewer, and Connect shows while the event has
 * connections on. `variant="organiser"` is the Requests tab's view, from the
 * attendee record, with neither a note nor Connect.
 *
 * `fallback` is what the caller already shows for the person, such as the
 * name and picture an agenda entry carries. It stands in when the person's
 * own record is gone (a presenter who withdrew), so the sheet names them
 * instead of saying they are not listed. It carries no contact details, no
 * note and no Connect.
 *
 * `children` is for what only one page does with the person, such as the
 * connections page's "Remove", and never for what the person is.
 */
export function EventPersonSheet({
  eventId,
  person,
  variant = "member",
  connectionUid,
  fallback,
  onClose,
  children,
}: {
  eventId: string;
  /** Who is open; null when the sheet is closed. */
  person: EventPersonTarget | null;
  variant?: "member" | "organiser";
  /** On the connections page: the other person's uid. */
  connectionUid?: string;
  /** Shown when the person's record is missing. */
  fallback?: { name: string; avatar?: string };
  onClose: () => void;
  children?: ReactNode;
}) {
  const state = useEventPerson(
    person ? { eventId, target: person, variant, connectionUid } : null,
  );
  return (
    <Sheet
      open={person !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent data-testid="person-sheet" data-variant={variant}>
        {person && state.status === "loading" && (
          <div className="space-y-6 pt-6" data-testid="person-sheet-loading">
            {/* Radix wants a title on every dialog, loading or not. */}
            <SheetHeader className="sr-only">
              <SheetTitle>Loading profile</SheetTitle>
              <SheetDescription>Loading profile</SheetDescription>
            </SheetHeader>
            <LoadingRows rows={3} />
          </div>
        )}
        {person && state.status === "missing" && fallback && (
          <PersonView
            eventId={eventId}
            person={{
              profile: { name: fallback.name, avatar: fallback.avatar },
              signals: [],
              notes: null,
              connect: null,
            }}
            variant={variant}
          >
            {children}
          </PersonView>
        )}
        {person && state.status === "missing" && !fallback && (
          <div
            className="flex flex-col items-start gap-3 pt-10 text-left"
            data-testid="person-sheet-missing"
          >
            <UserX className="h-8 w-8 text-muted-foreground" aria-hidden />
            <SheetHeader className="items-start">
              <SheetTitle>Not listed any more</SheetTitle>
              <SheetDescription>
                This person is no longer on the event&apos;s attendee list.
              </SheetDescription>
            </SheetHeader>
            {children}
          </div>
        )}
        {person && state.status === "ready" && (
          <PersonView eventId={eventId} person={state.person} variant={variant}>
            {children}
          </PersonView>
        )}
      </SheetContent>
    </Sheet>
  );
}

function PersonView({
  eventId,
  person,
  variant,
  children,
}: {
  eventId: string;
  person: EventPerson;
  variant: "member" | "organiser";
  children?: ReactNode;
}) {
  const uid = useSession()?.uid;
  const { profile } = person;
  const safeLinkedIn = safeLinkedInUrl(person.linkedinUrl);
  return (
    <div className="space-y-6 pt-6">
      {/* Left-aligned like the bio below it: a centred header over a left-aligned
          body read as two layouts. */}
      <SheetHeader className="items-start text-left">
        {profile.avatar ? (
          <img
            src={profile.avatar}
            alt=""
            className="h-24 w-24 rounded-full object-cover"
          />
        ) : (
          <AvatarPlaceholder name={profile.name} size="lg" />
        )}
        <SheetTitle data-testid="person-sheet-name" className="wrap-anywhere">
          {profile.name}
        </SheetTitle>
        <SheetDescription>
          {[profile.role, profile.company].filter(Boolean).join(" · ")}
        </SheetDescription>
      </SheetHeader>
      {person.signals.length > 0 && (
        <SignalBadges
          signals={person.signals}
          className="justify-start"
          testId="profile-signals"
        />
      )}
      {profile.bio && (
        <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
          {profile.bio}
        </p>
      )}
      {safeLinkedIn && (
        <a
          href={safeLinkedIn}
          target="_blank"
          rel="noopener noreferrer"
          className="flex min-h-11 items-center justify-center gap-2 rounded-md bg-muted px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted/70"
          data-testid="person-sheet-linkedin"
        >
          <LinkedInIcon className="h-4 w-4" />
          View LinkedIn profile
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
      {person.email && (
        <a
          href={`mailto:${person.email}`}
          className="flex min-h-11 min-w-0 items-center justify-center gap-2 rounded-md bg-muted px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted/70"
          data-testid="person-sheet-email"
        >
          <Mail className="h-4 w-4 shrink-0" />
          <span className="min-w-0 [overflow-wrap:anywhere]">
            {person.email}
          </span>
        </a>
      )}
      {variant === "member" && uid && person.connect && (
        <ConnectWithPerson
          uid={uid}
          event={person.connect.event}
          personName={profile.name}
          connected={person.connect.connected}
        />
      )}
      {variant === "member" && person.notes && (
        <PersonNotesSection
          eventId={eventId}
          attendeeId={person.notes.attendeeId}
          personName={profile.name}
          fallbackEvent={person.notes.fallbackEvent}
        />
      )}
      {children}
    </div>
  );
}
